import type { FC } from 'react'
import type { FeedPost } from '@/types'

import { Heart, MessageCircle, Send } from 'lucide-react'
import { Photo } from '@/components/kit/photo'
import { Spark } from '@/components/kit/spark'

interface FeedScreenProps {
    post: FeedPost
    handle: string
    index: number
    total: number
}

/**
 * Renders one designed social post inside a phone frame, from the
 * case study's feed data.
 */
export const FeedScreen: FC<FeedScreenProps> = ({ post, handle, index, total }) => {
    const header = (
        <div className='absolute inset-x-0 top-0 z-20 flex items-center justify-between px-4 pt-[17%]'>
            <span className='rounded-full bg-black/25 px-2 py-0.5 font-mono text-[9px] text-white backdrop-blur-md'>
                {handle}
            </span>
            <span className='font-mono text-[9px] text-white/90 mix-blend-difference'>
                {index + 1}/{total}
            </span>
        </div>
    )

    if (post.kind === 'photo') {
        return (
            <div className='absolute inset-0'>
                {post.image && (
                    <Photo
                        src={post.image}
                        alt=''
                        sizes='300px'
                        position={post.position}
                        className='absolute inset-0'
                    />
                )}
                <div className='absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/75' />
                {header}
                <div className='absolute bottom-[28%] right-3 z-10 flex flex-col items-center gap-3 text-white'>
                    <Heart className='size-5' />
                    <MessageCircle className='size-5' />
                    <Send className='size-5' />
                </div>
                <div className='absolute inset-x-0 bottom-0 z-10 p-4 pb-6 text-white'>
                    <p className='text-sm font-semibold leading-snug'>{post.caption}</p>
                    <span className='mt-3 block h-0.5 overflow-hidden rounded-full bg-white/30'>
                        <span className='block h-full w-1/2 rounded-full bg-white' />
                    </span>
                </div>
            </div>
        )
    }

    const background = post.bg ?? '#ebe6dd'
    const color = post.fg ?? '#1e1e1e'

    return (
        <div
            className='absolute inset-0 flex flex-col px-5 pb-7 pt-[30%]'
            style={{ backgroundColor: background, color }}
        >
            <div className='absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-[17%]'>
                <span className='font-mono text-[9px] opacity-70'>{handle}</span>
                <span className='font-mono text-[9px] opacity-70'>{index + 1}/{total}</span>
            </div>

            {post.kind === 'type' && (
                <>
                    {post.eyebrow && (
                        <span className='font-mono text-[10px] uppercase tracking-[0.16em] opacity-70'>{post.eyebrow}</span>
                    )}
                    <p className='mt-auto font-heading text-[1.9rem] font-extrabold leading-[0.95] tracking-[-0.035em]'>
                        {post.title}
                    </p>
                    <span className='mt-6 flex gap-1'>
                        {[0, 1, 2, 3, 4].map(dot => (
                            <span
                                key={dot}
                                className='h-1 flex-1 rounded-full'
                                style={{ backgroundColor: color, opacity: dot === 0 ? 1 : 0.2 }}
                            />
                        ))}
                    </span>
                </>
            )}

            {post.kind === 'stat' && (
                <>
                    <Spark className='size-6' />
                    <p className='mt-auto font-heading text-[4.25rem] font-extrabold leading-[0.85] tracking-[-0.04em]'>
                        {post.value}
                    </p>
                    <p className='mt-3 text-sm leading-snug opacity-80'>{post.label}</p>
                </>
            )}

            {post.kind === 'quote' && (
                <>
                    <span
                        aria-hidden='true'
                        className='font-heading text-7xl font-extrabold leading-none text-ember'
                    >
                        “
                    </span>
                    <p className='font-heading text-[1.55rem] font-bold leading-[1.05] tracking-[-0.03em]'>
                        {post.text}
                    </p>
                    <p className='mt-auto font-mono text-[10px] uppercase tracking-[0.14em] opacity-70'>{post.label}</p>
                </>
            )}
        </div>
    )
}
